/**
 * Client-side helpers for `GET /namespaces` (ENR-809 PR1, #868). Mirrors
 * the SDK shape; once `@enduring-systems/test-inbox-client` republishes
 * with `listNamespaces` the ops-console can switch over wholesale. Today
 * the SDK on the registry is 0.2.0 and predates this endpoint, so we
 * inline the fetch — same pattern as `fetchStats` / `fetchHealth`.
 */

export type NamespaceSortField = "createdAt" | "messageCount" | "lastReceivedAt";

export interface NamespaceListItem {
  id: string;
  env: string;
  tags: Record<string, string>;
  clientRef: string | null;
  createdAt: string;
  expiresAt: string;
  messageCount: number;
  lastReceivedAt: string | null;
}

export interface NamespaceList {
  items: NamespaceListItem[];
  /** Opaque cursor — pass back as `cursor` to fetch the next page. */
  nextCursor: string | null;
}

export interface ListNamespacesOptions {
  /** AND-combined tag filter; emitted as `tag.<key>=<value>` query params. */
  tags?: Record<string, string>;
  createdAfter?: string;
  createdBefore?: string;
  sort?: NamespaceSortField;
  limit?: number;
  cursor?: string;
}

export async function fetchNamespaces(
  baseUrl: string,
  opts: ListNamespacesOptions = {}
): Promise<NamespaceList> {
  const params = new URLSearchParams();
  if (opts.tags) {
    for (const [k, v] of Object.entries(opts.tags)) {
      if (k.length > 0 && v.length > 0) params.append(`tag.${k}`, v);
    }
  }
  if (opts.createdAfter) params.set("createdAfter", opts.createdAfter);
  if (opts.createdBefore) params.set("createdBefore", opts.createdBefore);
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);

  const qs = params.toString();
  const path = qs.length > 0 ? `/namespaces?${qs}` : "/namespaces";
  const res = await fetch(`${baseUrl}${path}`);
  if (!res.ok) {
    throw new Error(`fetchNamespaces failed with status ${res.status}`);
  }
  return (await res.json()) as NamespaceList;
}
